export const emptyDeliveryForm = {
    label: "",
    firstDeliveryDate: "",
    deliveryTime: "",
    frequency: "HEBDOMADAIRE",
    frequencyDays: 7,
    driverId: "",
    notes: "",
    targets: [],
};

export const DELIVERY_CALENDAR_THEME = {
    petrol: "#0C3B3C",
    petrolTint: "#e7edec",
    validation: "#17B978",
    validationDeep: "#0f9d6b",
    cream: "#F4F1EA",
    ink: "#122A26",
    gold: "#C8A44D",
    goldTint: "#faf3e2",
    gray: "#5A6D68",
    alert: "#B4432E",
    border: "rgba(12,59,60,.10)",
};

export function deliveryCadenceLabel(planning) {
    if (planning.frequency === "PONCTUELLE") return "Une seule fois";
    const days = Number(planning.frequencyDays) > 0 ? Number(planning.frequencyDays) : 7;
    return days === 1 ? "Tous les jours" : `Tous les ${days} jours`;
}

export function deliveryTargetKey(target) {
    return `${target.ehpadId}:${target.ehpadSubcategory || ""}`;
}

export function deliveryTargetLabel(target) {
    return target.ehpadSubcategory ? `${target.code} · ${target.ehpadSubcategory}` : target.code;
}

export function formatDeliveryDate(value) {
    if (!value) return "—";
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime())
        ? value
        : date.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}

export function formatDeliveryTime(value) {
    return value ? value.slice(0, 5) : "Heure non définie";
}

export function sortDeliveryPlannings(items) {
    return [...items].sort((a, b) => {
        const dateCompare = String(a.firstDeliveryDate || "").localeCompare(String(b.firstDeliveryDate || ""));
        if (dateCompare !== 0) return dateCompare;
        const timeCompare = String(a.deliveryTime || "").localeCompare(String(b.deliveryTime || ""));
        if (timeCompare !== 0) return timeCompare;
        return String(a.label || "").localeCompare(String(b.label || ""));
    });
}

export function dateKeyFromDate(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export function addMonths(date, amount) {
    return new Date(date.getFullYear(), date.getMonth() + amount, 1);
}

export function addWeeks(date, amount) {
    const next = new Date(date);
    next.setDate(next.getDate() + amount * 7);
    return next;
}

export function startOfDeliveryWeek(date) {
    const start = new Date(date);
    const mondayOffset = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - mondayOffset);
    return start;
}

export function deliveryCalendarLabel(date, view) {
    if (view === "week") {
        const start = startOfDeliveryWeek(date);
        const end = new Date(start);
        end.setDate(start.getDate() + 6);
        return `${start.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })} - ${end.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}`;
    }
    return date.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
}

export function buildDeliveryCalendarDays(monthDate) {
    const firstOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
    const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
    const start = new Date(firstOfMonth);
    start.setDate(firstOfMonth.getDate() - mondayOffset);
    return Array.from({ length: 42 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return {
            date,
            key: dateKeyFromDate(date),
            inMonth: date.getMonth() === monthDate.getMonth(),
        };
    });
}

export function buildDeliveryWeekDays(anchorDate) {
    const start = startOfDeliveryWeek(anchorDate);
    return Array.from({ length: 7 }, (_, index) => {
        const date = new Date(start);
        date.setDate(start.getDate() + index);
        return {
            date,
            key: dateKeyFromDate(date),
            inMonth: true,
        };
    });
}

export function addDaysToDateKey(key, days) {
    const date = new Date(`${key}T00:00:00`);
    date.setDate(date.getDate() + days);
    return dateKeyFromDate(date);
}

export const DELIVERY_OCCURRENCE_WINDOW_DAYS = 30;

export function buildUpcomingDeliveryOccurrences(
    planning,
    todayKey = dateKeyFromDate(new Date()),
    windowDays = DELIVERY_OCCURRENCE_WINDOW_DAYS
) {
    if (!planning.firstDeliveryDate) return [];
    const frequency = planning.frequency || "HEBDOMADAIRE";
    const frequencyDays = Number(planning.frequencyDays) > 0 ? Number(planning.frequencyDays) : 7;
    const windowEndKey = addDaysToDateKey(todayKey, windowDays);
    const overridesByOriginalDate = new Map(
        (planning.occurrenceOverrides ?? [])
            .filter(override => override.originalDate && override.movedDate)
            .map(override => [override.originalDate, override.movedDate])
    );
    const occurrences = [];
    let originalDate = planning.firstDeliveryDate;
    let guard = 0;

    while (originalDate && guard < 320) {
        const movedDate = overridesByOriginalDate.get(originalDate);
        const displayDate = movedDate || originalDate;
        if (displayDate >= todayKey && displayDate <= windowEndKey) {
            occurrences.push({ originalDate, displayDate, moved: Boolean(movedDate) });
        }
        if (frequency === "PONCTUELLE") break;
        if (originalDate > windowEndKey && displayDate > windowEndKey) break;
        originalDate = addDaysToDateKey(originalDate, frequencyDays);
        guard += 1;
    }
    return occurrences;
}

export function buildDeliveryOccurrences(plannings, calendarDays) {
    const visibleKeys = new Set(calendarDays.map(day => day.key));
    if (!calendarDays.length) return {};

    return plannings.reduce((acc, planning) => {
        buildUpcomingDeliveryOccurrences(planning).forEach(occurrence => {
            if (!visibleKeys.has(occurrence.displayDate)) return;
            acc[occurrence.displayDate] = [
                ...(acc[occurrence.displayDate] || []),
                { ...planning, occurrenceDate: occurrence.originalDate, displayDate: occurrence.displayDate, moved: occurrence.moved },
            ];
        });
        return acc;
    }, {});
}

export function deliveryModalTitle(editingId, modalKind) {
    if (editingId) return "Modifier une livraison";
    if (modalKind === "exceptional") return "Ajouter une livraison exceptionnelle";
    return "Ajouter une livraison";
}
